/**
 * Shadow Breaker Pro Dashboard Overlay — "Vector Gauge" design
 * Unique layout: hexagonal signal badge + horizontal risk/reward meter that
 * plots the live price between the effective stop and the single target,
 * with a tick for entry and a moving marker for current price. Distinct
 * from the base Shadow Breaker's vertical price-ladder HUD.
 * CSS prefix: sbp-
 */
import React from "react";
import { ShadowBreakerProDashboard } from "../../indicators/built-in/shadow-breaker-pro";
import "./ShadowBreakerProOverlay.scss";

interface ShadowBreakerProOverlayProps {
  dashboard: ShadowBreakerProDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short" }[];
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

const ShadowBreakerProOverlay: React.FC<ShadowBreakerProOverlayProps> = ({
  dashboard, symbol, visible,
  showDashboard = true, onToggleDashboard, arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const {
    signal, entry, stopLoss, trailingStop, target, currentPrice,
    livePL, bookedPL, status, targetHit, slHit, tslHit, isReversal,
  } = dashboard;

  const isLong = signal === "BUY";
  const isShort = signal === "SELL";
  const isActive = status === "Active";
  const hasTrade = entry !== null && stopLoss !== null && target !== null;

  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => (v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2));

  const themeClass = isLong ? "sbp-buy" : isShort ? "sbp-sell" : "sbp-neutral";

  const statusText =
    status === "Target Hit" ? "TARGET BREACHED"
    : status === "Stop Loss Hit" ? "STOP LOSS HIT"
    : status === "Trailing SL Hit" ? "TRAILING SL HIT"
    : status === "Active" ? "TRADE ACTIVE"
    : "SCANNING…";

  const statusMod =
    status === "Active" || status === "Waiting" ? ""
    : status === "Target Hit" ? " sbp-status--tgt"
    : status === "Stop Loss Hit" ? " sbp-status--sl"
    : " sbp-status--tsl";

  // ─── Horizontal risk/reward gauge math ──────────────────────────
  // The gauge always reads left(risk/red) → right(reward/green), i.e. 0% is
  // the CURRENT effective stop (tighter of fixed SL / TSL) and 100% is the
  // Target — regardless of whether the stop is numerically above or below
  // the target. For a SELL, the stop sits at a HIGHER price than the target,
  // so naively mapping low-price→0%/high-price→100% (as for a BUY) would
  // put the stop side on the right and the target side on the left,
  // inverting the red/green zones. Direction-aware progress fixes that.
  let gaugeProgress = 0.5;
  let entryProgress = 0.5;
  let tslProgress: number | null = null;
  if (hasTrade) {
    const effStop = isLong
      ? Math.max(stopLoss!, trailingStop ?? stopLoss!)
      : Math.min(stopLoss!, trailingStop ?? stopLoss!);
    const span = (isLong ? target! - effStop : effStop - target!) || 1;
    const toProgress = (price: number) =>
      clamp01(isLong ? (price - effStop) / span : (effStop - price) / span);
    gaugeProgress = toProgress(currentPrice);
    entryProgress = toProgress(entry!);
    if (trailingStop !== null && trailingStop !== stopLoss) {
      tslProgress = toProgress(trailingStop);
    }
  }

  return (
    <>
      {/* Arrow Badges */}
      {arrowBadges.map((badge, i) => (
        <div
          key={i}
          className={`sbp-arrow-badge sbp-arrow-badge--${badge.direction}`}
          style={{
            left: badge.x,
            top: badge.direction === "long" ? badge.y + 30 : badge.y - 44,
          }}
        >
          {badge.direction === "long" ? "Buy" : "Sell"}
        </div>
      ))}

      <div className={`sbp-overlay ${themeClass}${showDashboard ? "" : " sbp-overlay--collapsed"}`}>
        <div className="sbp-edge-glow" />

        {/* Header */}
        <div className="sbp-header">
          <span className="sbp-brand-icon">◆</span>
          <span className="sbp-title">SHADOW BREAKER</span>
          <span className="sbp-pro-badge">PRO</span>
          <button
            className="sbp-toggle-btn"
            onClick={onToggleDashboard}
            title={showDashboard ? "Hide dashboard" : "Show dashboard"}
            aria-label={showDashboard ? "Hide dashboard" : "Show dashboard"}
          >
            <span className={`sbp-toggle-icon${showDashboard ? " sbp-toggle-icon--open" : ""}`}>&#x25BE;</span>
          </button>
        </div>

        {showDashboard && (
          <>
            {/* Symbol + Price */}
            <div className="sbp-quote-row">
              <span className="sbp-symbol">{symbol}</span>
              <span className="sbp-cmp">{currentPrice.toFixed(2)}</span>
            </div>

            {/* Hexagonal signal badge + P&L split */}
            <div className="sbp-signal-wrap">
              <div className={`sbp-hex${isActive ? " sbp-hex--pulse" : ""}`}>
                <span className="sbp-hex-arrow">{isLong ? "▲" : isShort ? "▼" : "•"}</span>
                <span className="sbp-hex-label">{signal ?? "FLAT"}</span>
              </div>
              <div className="sbp-pl-split">
                <div className="sbp-pl-cell">
                  <span className="sbp-pl-lbl">LIVE</span>
                  <span className={`sbp-pl-val ${livePL >= 0 ? "sbp-profit" : "sbp-loss"}`}>{fmtPL(livePL)}</span>
                </div>
                <div className="sbp-pl-cell">
                  <span className="sbp-pl-lbl">BOOKED</span>
                  <span className={`sbp-pl-val ${bookedPL >= 0 ? "sbp-profit" : "sbp-loss"}`}>{fmtPL(bookedPL)}</span>
                </div>
              </div>
            </div>

            {isReversal && hasTrade && (
              <div className="sbp-reversal-tag">
                <span className="sbp-reversal-icon">&#x27F2;</span> REVERSAL ENTRY
              </div>
            )}

            {/* Horizontal risk/reward gauge */}
            <div className="sbp-gauge">
              <div className="sbp-gauge-track">
                <div className="sbp-gauge-zone sbp-gauge-zone--risk" style={{ width: `${entryProgress * 100}%` }} />
                <div className="sbp-gauge-zone sbp-gauge-zone--reward" style={{ width: `${(1 - entryProgress) * 100}%` }} />
                <div className="sbp-gauge-tick sbp-gauge-tick--entry" style={{ left: `${entryProgress * 100}%` }} />
                {tslProgress !== null && (
                  <div className="sbp-gauge-tick sbp-gauge-tick--tsl" style={{ left: `${tslProgress * 100}%` }} />
                )}
                <div
                  className={`sbp-gauge-marker${isActive ? " sbp-gauge-marker--pulse" : ""}`}
                  style={{ left: `${gaugeProgress * 100}%` }}
                  title={`CMP ${currentPrice.toFixed(2)}`}
                />
              </div>
              <div className="sbp-gauge-labels">
                <div className="sbp-gauge-cell sbp-gauge-cell--sl">
                  <span className="sbp-gauge-cell-lbl">STOP{slHit ? " ✗" : ""}</span>
                  <span className="sbp-gauge-cell-val">{fmt(stopLoss)}</span>
                </div>
                <div className="sbp-gauge-cell sbp-gauge-cell--tsl">
                  <span className="sbp-gauge-cell-lbl">TRAIL{tslHit ? " ✗" : ""}</span>
                  <span className="sbp-gauge-cell-val">{fmt(trailingStop)}</span>
                </div>
                <div className="sbp-gauge-cell sbp-gauge-cell--entry">
                  <span className="sbp-gauge-cell-lbl">ENTRY</span>
                  <span className="sbp-gauge-cell-val">{fmt(entry)}</span>
                </div>
                <div className="sbp-gauge-cell sbp-gauge-cell--tgt">
                  <span className="sbp-gauge-cell-lbl">TARGET{targetHit ? " ✓" : ""}</span>
                  <span className="sbp-gauge-cell-val">{fmt(target)}</span>
                </div>
              </div>
            </div>

            {/* Status ribbon */}
            <div className={`sbp-status${statusMod}`}>
              <span className="sbp-status-text">{statusText}</span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ShadowBreakerProOverlay;
